import { ref, watch, type Ref } from 'vue'
import { usePrefixedStorage } from './usePrefixedStorage'
import type { PriceZone } from './useZone'

export interface HourPrice {
  /** Hour of day, 0–23, parsed from the API's "00".."23" strings. */
  hour: number
  /** Spot price in DKK per kWh (excluding tariffs and taxes). */
  price: number
}

interface RawHourPrice {
  hour: string
  price: number
}

interface GridPricesResponseShape {
  [date: string]: {
    DK1?: RawHourPrice[]
    DK2?: RawHourPrice[]
  }
}

interface CacheEntry {
  fetchedAt: number
  data: { DK1: HourPrice[]; DK2: HourPrice[] }
}

const CACHE_TTL_MS = 5 * 60 * 1000

function parseHour(value: string): number {
  const n = Number.parseInt(value, 10)
  if (Number.isNaN(n) || n < 0 || n > 23) {
    throw new Error(`Invalid hour value: ${value}`)
  }
  return n
}

function parseResponse(
  raw: GridPricesResponseShape,
  date: string,
): { DK1: HourPrice[]; DK2: HourPrice[] } {
  const dayBucket = raw[date]
  if (!dayBucket) {
    throw new Error(`No price data for ${date}`)
  }
  const dk1 = dayBucket.DK1 ?? []
  const dk2 = dayBucket.DK2 ?? []
  if (dk1.length === 0 && dk2.length === 0) {
    throw new Error(`Empty price data for ${date}`)
  }
  return {
    DK1: dk1.map((p) => ({ hour: parseHour(p.hour), price: p.price })),
    DK2: dk2.map((p) => ({ hour: parseHour(p.hour), price: p.price })),
  }
}

/** Returns YYYY-MM-DD for the given date in Europe/Copenhagen timezone. */
export function copenhagenDateString(input: Date = new Date()): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Copenhagen',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return formatter.format(input)
}

/** Returns the current hour 0–23 in Europe/Copenhagen timezone. */
export function copenhagenHour(input: Date = new Date()): number {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Copenhagen',
    hour: '2-digit',
    hour12: false,
  })
  return Number.parseInt(formatter.format(input), 10)
}

function nextDateString(date: string): string {
  const [y, m, d] = date.split('-').map((s) => Number.parseInt(s, 10))
  const dt = new Date(Date.UTC(y!, m! - 1, d!))
  dt.setUTCDate(dt.getUTCDate() + 1)
  return copenhagenDateString(dt)
}

export interface UseGridPricesOptions {
  /** Base URL for the Greenbow API. */
  apiBaseUrl: string
  /** Active price zone. Reactively swaps which prices are exposed. */
  zone: Ref<PriceZone>
  /** is_green=1 fetches green-only prices. Default false. */
  isGreen?: boolean
  /** Storage prefix for the 5-minute cache. Defaults to 'smartcharge'. */
  cachePrefix?: string
  /** Override the current date — useful for tests. */
  now?: () => Date
}

export interface UseGridPricesReturn {
  today: Ref<HourPrice[]>
  tomorrow: Ref<HourPrice[] | null>
  loading: Ref<boolean>
  error: Ref<Error | null>
  refresh: () => Promise<void>
}

export function useGridPrices(options: UseGridPricesOptions): UseGridPricesReturn {
  const { apiBaseUrl, zone, isGreen = false, cachePrefix = 'smartcharge' } = options
  const now = options.now ?? (() => new Date())

  const storage = usePrefixedStorage(cachePrefix, 'session')

  const todayDK1 = ref<HourPrice[]>([])
  const todayDK2 = ref<HourPrice[]>([])
  const tomorrowDK1 = ref<HourPrice[] | null>(null)
  const tomorrowDK2 = ref<HourPrice[] | null>(null)

  const today = ref<HourPrice[]>([])
  const tomorrow = ref<HourPrice[] | null>(null)
  const loading = ref(false)
  const error = ref<Error | null>(null)

  function applyZone() {
    today.value = zone.value === 'DK1' ? todayDK1.value : todayDK2.value
    tomorrow.value = zone.value === 'DK1' ? tomorrowDK1.value : tomorrowDK2.value
  }

  watch(zone, applyZone)

  function cacheKey(date: string): string {
    return `gridprices:${date}:${isGreen ? 1 : 0}`
  }

  function readCache(date: string): { DK1: HourPrice[]; DK2: HourPrice[] } | null {
    const entry = storage.get<CacheEntry>(cacheKey(date))
    if (!entry) return null
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) return null
    return entry.data
  }

  function writeCache(date: string, data: { DK1: HourPrice[]; DK2: HourPrice[] }): void {
    const entry: CacheEntry = { fetchedAt: Date.now(), data }
    storage.set(cacheKey(date), entry)
  }

  async function fetchDate(date: string): Promise<{ DK1: HourPrice[]; DK2: HourPrice[] }> {
    const cached = readCache(date)
    if (cached) return cached

    const url = `${apiBaseUrl}/v1/web/grid-prices?date=${date}&is_green=${isGreen ? 1 : 0}`
    const response = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }
    const raw = (await response.json()) as GridPricesResponseShape
    const parsed = parseResponse(raw, date)
    writeCache(date, parsed)
    return parsed
  }

  async function refresh(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const todayDate = copenhagenDateString(now())
      const todayData = await fetchDate(todayDate)
      todayDK1.value = todayData.DK1
      todayDK2.value = todayData.DK2

      // Tomorrow becomes available around 12:00 Europe/Copenhagen.
      if (copenhagenHour(now()) >= 12) {
        try {
          const tomorrowDate = nextDateString(todayDate)
          const tomorrowData = await fetchDate(tomorrowDate)
          tomorrowDK1.value = tomorrowData.DK1
          tomorrowDK2.value = tomorrowData.DK2
        } catch {
          // Tomorrow is best-effort — silently drop if not yet available
          tomorrowDK1.value = null
          tomorrowDK2.value = null
        }
      } else {
        tomorrowDK1.value = null
        tomorrowDK2.value = null
      }

      applyZone()
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err))
    } finally {
      loading.value = false
    }
  }

  return { today, tomorrow, loading, error, refresh }
}
